import { assign, isEmpty, isEqual, isFunction, isString, isUndefined, keys } from 'lodash';

import {
    DEFAULT_VALUE_MAX,
    DEFAULT_VALUE_MIN,
    Increment,
    RatingColorByValueMap,
    RatingColorCallback,
    Size,
} from 'core/rating/types';
import { getPotentialIncrementedValues, getPotentialIntegerValues, parseRangeValue } from 'core/rating/util/Values';
import { Color } from 'core/util/Variables';

export const parseColorProp = (
    fallbackValue: Color,
    prop?: Color | string | RatingColorCallback,
): string | RatingColorCallback => {
    if (isFunction(prop) || isString(prop)) {
        return prop;
    }

    return fallbackValue;
};

export const parseColorEmpty = (prop?: Color | string | RatingColorCallback): string | RatingColorCallback => {
    return parseColorProp(Color.Grey5, prop);
};

export const parseColorFull = (prop?: Color | string | RatingColorCallback): string | RatingColorCallback => {
    return parseColorProp(Color.Yellow1, prop);
};

export const applyColorProp = (prop: Color | string | RatingColorCallback, selectedValue?: number | null): string => {
    return isFunction(prop) ? prop(selectedValue) : prop;
};

export const parseHtmlName = (prop?: string | null): string => {
    if (isEmpty(prop)) {
        return 'rating';
    }

    return prop as string;
};

export const parseIconEmpty = (prop?: string): string => {
    if (isUndefined(prop)) {
        return 'icon-star';
    }

    return prop;
};

export const parseIconFull = (prop?: string): string => {
    if (isUndefined(prop)) {
        return 'icon-star-solid';
    }

    return prop;
};

export const parseIncrement = (prop?: Increment): Increment => {
    if (isUndefined(prop)) {
        return Increment.Point;
    }

    return prop;
};

export const parseIsReadOnly = (prop?: boolean): boolean => {
    if (isUndefined(prop)) {
        return false;
    }

    return prop;
};

export const parseSize = (prop?: Size): Size => {
    if (isUndefined(prop)) {
        return Size.Medium;
    }

    return prop;
};

export const parseTabIndex = (prop?: number, isReadOnly?: boolean): number => {
    if (isReadOnly) {
        return -1;
    }
    if (isUndefined(prop)) {
        return 0;
    }

    return prop;
};

export const parseValueMax = (prop?: number): number => parseRangeValue(prop, DEFAULT_VALUE_MAX);

export const parseValueMin = (prop?: number): number => parseRangeValue(prop, DEFAULT_VALUE_MIN);

export const getColorByValueMap = (
    potentialValues: number[],
    prop: string | RatingColorCallback,
): RatingColorByValueMap => {
    const result: RatingColorByValueMap = {};

    potentialValues.forEach((value) => {
        result[value] = applyColorProp(prop, value);
    });

    return result;
};
export const parseColorByValueMap = (
    colorFn: string | RatingColorCallback,
    prop?: RatingColorByValueMap,
    potentialValues: number[] = [],
    increment: Increment = parseIncrement(),
): RatingColorByValueMap => {
    const isPropDefined = !isUndefined(prop);
    let potentialValuesWithIncrements: number[] = [];

    potentialValues.forEach((potentialValue) => {
        const incrementedValues = getPotentialIncrementedValues(potentialValue, increment);

        potentialValuesWithIncrements = potentialValuesWithIncrements.concat(incrementedValues);
    });

    if (isPropDefined && isEqual(keys(prop), potentialValuesWithIncrements)) {
        return prop as RatingColorByValueMap;
    }

    potentialValuesWithIncrements.sort();

    const result = getColorByValueMap(potentialValuesWithIncrements, colorFn);

    if (isPropDefined) {
        assign(result, prop);
    }

    return result;
};

export const parseColorEmptyByValueMap = (
    prop?: RatingColorByValueMap,
    potentialValues: number[] = [],
    increment: Increment = parseIncrement(),
    colorFn?: string | RatingColorCallback,
): RatingColorByValueMap => parseColorByValueMap(parseColorEmpty(colorFn), prop, potentialValues, increment);

export const parseColorFullByValueMap = (
    prop?: RatingColorByValueMap,
    potentialValues: number[] = [],
    increment: Increment = parseIncrement(),
    colorFn?: string | RatingColorCallback,
): RatingColorByValueMap => parseColorByValueMap(parseColorFull(colorFn), prop, potentialValues, increment);

export const DEFAULT_COLOR_EMPTY = parseColorEmpty();
export const DEFAULT_COLOR_FULL = parseColorFull();
export const DEFAULT_INCREMENT = parseIncrement();
export { DEFAULT_VALUE_MAX, DEFAULT_VALUE_MIN } from 'core/rating/types';
export const DEFAULT_POTENTIAL_VALUES = getPotentialIntegerValues(DEFAULT_VALUE_MIN, DEFAULT_VALUE_MAX);
export const DEFAULT_COLOR_EMPTY_BY_VALUE = parseColorEmptyByValueMap(
    undefined,
    DEFAULT_POTENTIAL_VALUES,
    DEFAULT_INCREMENT,
    DEFAULT_COLOR_EMPTY,
);
export const DEFAULT_COLOR_FULL_BY_VALUE = parseColorEmptyByValueMap(
    undefined,
    DEFAULT_POTENTIAL_VALUES,
    DEFAULT_INCREMENT,
    DEFAULT_COLOR_FULL,
);
